import styled from "@emotion/styled";
import { MouseEvent, useEffect, useRef, useState } from "react";
import { Alert, MenuItem, menuItemClasses, Typography } from "@kaltura/ds-react-components";
import { Check16Icon, Globe24Icon, Link24Icon, Lock24Icon } from "@kaltura/ds-react-icons";
import { HtmlReactParser, translate } from "@kaltura/mediaspace-shared-utils";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import {
    ButtonClickAnalyticsType,
    KmsTypeCategoryMembership,
    PublishSaveResponseData,
    PublishStatus,
    SharedMenuItemProps,
} from "@kaltura/mediaspace-shared-types";
import { ModalManager } from "./modals/ModalManager";

export interface PublishMenuItemsProps extends SharedMenuItemProps {
    entryIds: string[];
    /**
     * current publishStatus is undefined in bulk publish
     */
    publishStatus?: PublishStatus;
    enableUnlisted?: boolean;
    crossApplicationUnpublishing?: boolean;
    allowUnpublishPublished?: boolean;
    allowPublish?: boolean;
    allowUnlisted?: boolean;
    warningMessages?: string[];
    onCloseMenu?: () => void;
    onItemStateChange?: (key: string, value: any) => void;
    reloadMedia?: () => void;
    /**
     * boolean flag to indicate bulk publish
     * using entryIds->length is not enough
     * because a single entry might be selected in bulk
     */
    isBulk?: boolean;
    disabledCategoryMembershipIds?: KmsTypeCategoryMembership[];
}

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
    height: "auto",
    maxWidth: 300,
    "& > div": {
        height: "auto",
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
    },
    [`& .${menuItemClasses.title}`]: {
        display: "flex",
    },
}));

const StyledAlertMenuItem = styled(MenuItem)(({ theme }) => ({
    height: "auto",
    maxWidth: 300,
    marginTop: theme.spacing(1),
    "&:focus, &:focus > div, &:hover, &:hover > div": {
        backgroundColor: "transparent",
    },
    "& > div": {
        height: "auto",
    },
}));

const StyledIconWrapper = styled("span")(({ theme }) => ({
    marginRight: theme.spacing(1),
}));

const StyledLabelWrapper = styled("span")({
    display: "flex",
    flexDirection: "column",
    maxWidth: 236,
    textWrap: "wrap",
});

const StyledTypography = styled(Typography)(({ theme }) => ({
    color: theme.kaltura.palette.tone2,
}));

const StyledAlert = styled(Alert)(({ theme }) => ({
    whiteSpace: "break-spaces",
    cursor: "default",
    "& a": {
        fontWeight: theme.typography.fontWeightBold,
        color: theme.kaltura.palette.tone1,
        textDecoration: "underline",
        cursor: "pointer",
        "&:hover": {
            color: theme.kaltura.palette.tone2,
            textDecoration: "none",
        },
    },
}));

/**
 * Publish indication menu items
 *
 * Meant to be used in menu or in nested menu items
 */
export function usePublishMenuItems(props: PublishMenuItemsProps) {
    const {
        entryIds,
        publishStatus,
        enableUnlisted = true,
        crossApplicationUnpublishing = false,
        allowUnpublishPublished = true,
        allowPublish = true,
        allowUnlisted = true,
        warningMessages = [],
        onCloseMenu,
        onItemStateChange,
        reloadMedia,
        isBulk = false,
        disabledCategoryMembershipIds = [],
    } = props;

    const [open, setOpen] = useState(false);
    const [targetStatus, setTargetStatus] = useState<PublishStatus>();
    const wasSubmitted = useRef(false);

    /**
     * reload my media list after publish request was submitted
     * done here instead of in below handleSubmit to allow media actions menu
     * and publish modal to close and only then reload the media list
     */
    useEffect(() => {
        return () => {
            if (wasSubmitted.current) {
                reloadMedia?.();
            }
        };
    }, []);

    const sendButtonAnalytics = useButtonAnalytics();

    // current publishStatus is not relevant for bulk publish
    const isPrivate = publishStatus === PublishStatus.PRIVATE;
    const isUnlisted = publishStatus === PublishStatus.UNLISTED;
    const isPublished = publishStatus === PublishStatus.PUBLISHED;

    const entryId = entryIds.length === 1 ? entryIds[0] : undefined;

    const handleSubmit = (result: PublishSaveResponseData) => {
        // reload media list after publish submit from my-media
        // see above useEffect for more details
        wasSubmitted.current = true;
        // update media page actions publish menu state
        // not relevant for bulk publish
        if (!isBulk) {
            onItemStateChange?.("publishStatus", result.publishStatus);
            onItemStateChange?.("warningMessages", result.warningMessages);
        }
        // close modal
        setOpen(false);
        // close menu
        onCloseMenu?.();
    };

    const handleClose = () => {
        // close modal
        setOpen(false);
    };

    const handlePrivateClick = () => {
        isPrivate && onCloseMenu?.();
        setOpen(!isPrivate);
        setTargetStatus(PublishStatus.PRIVATE);
        !isPrivate
            && sendButtonAnalytics(
                `Publishing indicator menu - Private ${isBulk ? "(bulk)" : ""}`,
                ButtonClickAnalyticsType.CHOOSE,
                entryId
            );
    };

    const handleUnlistedClick = () => {
        isUnlisted && onCloseMenu?.();
        setOpen(!isUnlisted);
        setTargetStatus(PublishStatus.UNLISTED);
        !isUnlisted
            && sendButtonAnalytics(
                `Publishing indicator menu - Unlisted ${isBulk ? "(bulk)" : ""}`,
                ButtonClickAnalyticsType.CHOOSE,
                entryId
            );
    };

    const handlePublishClick = () => {
        setOpen(true);
        setTargetStatus(PublishStatus.PUBLISHED);
        isPublished
            ? sendButtonAnalytics(
                  "Publishing indicator menu - Update publish locations",
                  ButtonClickAnalyticsType.CHOOSE,
                  entryId
              )
            : sendButtonAnalytics(
                  `Publishing indicator menu - Publish ${isBulk ? "(bulk)" : ""}`,
                  ButtonClickAnalyticsType.CHOOSE,
                  entryId
              );
    };

    const handleWarningClick = (event: MouseEvent<HTMLElement>) => {
        // beacon text supplied by legacy kms publish code
        // warnings are not shown in bulk publish
        const beacon = (event.target as HTMLElement).dataset?.beacon;
        if (beacon) {
            sendButtonAnalytics(beacon, ButtonClickAnalyticsType.EDIT, entryId);
        }
    };

    return [
        <StyledMenuItem
            key="private"
            className="kms-ds-publish-menu-item-private"
            onClick={handlePrivateClick}
            selected={isPrivate}
            icon={isPrivate ? <Check16Icon /> : undefined}
            aria-current={isPrivate}
            disabled={!allowUnpublishPublished && isPublished}
        >
            <StyledIconWrapper>
                <Lock24Icon />
            </StyledIconWrapper>
            <StyledLabelWrapper>
                {translate("Private")}
                <StyledTypography variant="body2">
                    {getPublishStatusDescription(PublishStatus.PRIVATE)}
                </StyledTypography>
            </StyledLabelWrapper>
        </StyledMenuItem>,
        enableUnlisted && (
            <StyledMenuItem
                key="unlisted"
                className="kms-ds-publish-menu-item-unlisted"
                onClick={handleUnlistedClick}
                selected={isUnlisted}
                icon={isUnlisted ? <Check16Icon /> : undefined}
                aria-current={isUnlisted}
                disabled={!allowUnlisted || (!allowUnpublishPublished && isPublished)}
            >
                <StyledIconWrapper>
                    <Link24Icon />
                </StyledIconWrapper>
                <StyledLabelWrapper>
                    {translate("Unlisted")}
                    <StyledTypography variant="body2">
                        {getPublishStatusDescription(PublishStatus.UNLISTED)}
                    </StyledTypography>
                </StyledLabelWrapper>
            </StyledMenuItem>
        ),
        <StyledMenuItem
            key="publish"
            className="kms-ds-publish-menu-item-publish"
            onClick={handlePublishClick}
            selected={isPublished}
            icon={isPublished ? <Check16Icon /> : undefined}
            aria-current={isPublished}
            disabled={!allowPublish}
        >
            <StyledIconWrapper>
                <Globe24Icon />
            </StyledIconWrapper>
            <StyledLabelWrapper>
                {isPublished ? translate("Change publish locations") : translate("Publish")}
                <StyledTypography variant="body2">
                    {getPublishStatusDescription(PublishStatus.PUBLISHED)}
                </StyledTypography>
            </StyledLabelWrapper>
        </StyledMenuItem>,
        ...warningMessages.map((warningMessage, index) => (
            <StyledAlertMenuItem key={`warn_${index}`} onClick={handleWarningClick}>
                <StyledAlert severity="warning" icon={false}>
                    {HtmlReactParser(warningMessage)}
                </StyledAlert>
            </StyledAlertMenuItem>
        )),
        // mount modals to force data fetch
        open && !!targetStatus && (
            <ModalManager
                key={"modal-manager"}
                entryIds={entryIds}
                crossApplicationUnpublishing={crossApplicationUnpublishing}
                publishStatus={publishStatus}
                targetStatus={targetStatus}
                onSubmit={handleSubmit}
                onClose={handleClose}
                isBulk={isBulk}
                disabledCategoryMembershipIds={disabledCategoryMembershipIds}
            />
        ),
    ];
}

export function getPublishStatusDescription(status: PublishStatus) {
    switch (status) {
        case PublishStatus.PRIVATE:
            return translate("Only I can view");
        case PublishStatus.UNLISTED:
            return translate("Anyone with the link can view");
        case PublishStatus.PUBLISHED:
            return translate("Anyone with entitlements to the gallery/channel can view");
    }
}

export default usePublishMenuItems;
